import React from 'react';
import { Tooltip } from 'src/components/PulseUI/Tooltip';

import { ImpersonateButton } from '../ImpersonateButton';
import { ResendInviteButton } from '../ResendInviteButton';
import { PasswordResetButton } from '../PasswordResetButton';
import { DeactivateButton } from '../DeactivateButton';
// Deep import on purpose: the UserTable barrel would close an import cycle.
import { getUserStatus } from '../UserTable/utils';

interface ActionButtonsProps {
  showImpersonate?: boolean;
  selectedUser?: MedScout.User;
  isHovered?: boolean;
  /** Resend invite + password reset — `userManagementCapabilities.canWrite`. */
  canWrite?: boolean;
  /** Deactivate the row's user — `userManagementCapabilities.canDeactivate`. */
  canDeactivate?: boolean;
}

/**
 * @description Row-level user actions, each gated on the viewer's user-management
 * capability (MKT-411). Resend invite, password reset and deactivate were
 * previously ungated and rendered for every viewer.
 */
const ActionButtons = ({
  showImpersonate = false,
  selectedUser,
  isHovered,
  canWrite = false,
  canDeactivate = false,
}: ActionButtonsProps) => {
  // Nothing to offer this viewer — don't render the hover affordance at all.
  if (!canWrite && !canDeactivate && !showImpersonate) return null;

  // Only an invited user has an outstanding invite to resend; an active user
  // wants a password reset, and a deactivated one should not be invited back in
  // without being reactivated first (MKT-383).
  const canResendInvite = canWrite && getUserStatus(selectedUser) === 'invited';

  return (
    <div
      className={[
        'absolute right-2 top-1/2 -translate-y-1/2 z-50',
        'bg-white rounded-full shadow-sm flex items-center justify-center',
        'px-2 py-0 gap-0.5',
        'transition-opacity duration-300 ease-in-out',
        isHovered
          ? 'opacity-100 pointer-events-auto border border-neutral-300'
          : 'opacity-0 pointer-events-none border border-transparent',
      ].join(' ')}
      onClick={(e) => e.stopPropagation()}
      data-testid="user-row-actions"
    >
      <div className="flex items-center justify-center ">
        {canResendInvite && (
          <Tooltip content="Resend Invite">
            <span onClick={(e) => e.stopPropagation()}>
              <ResendInviteButton
                userId={selectedUser?.id?.toString()}
                variant="icon"
              />
            </span>
          </Tooltip>
        )}
        {canWrite && (
          <Tooltip content="Send Password Reset">
            <span onClick={(e) => e.stopPropagation()}>
              <PasswordResetButton
                userId={selectedUser?.id?.toString()}
                variant="icon"
              />
            </span>
          </Tooltip>
        )}
        {/* No wrapping tooltip: the button carries its own, which also explains
            why a blocked deactivation is disabled (MKT-384). */}
        {canDeactivate && (
          <span onClick={(e) => e.stopPropagation()}>
            <DeactivateButton selectedUser={selectedUser} variant="icon" />
          </span>
        )}
        {/* No wrapping tooltip: the button's own names the row's user. */}
        {showImpersonate && (
          <span onClick={(e) => e.stopPropagation()}>
            <ImpersonateButton
              userId={selectedUser?.id?.toString()}
              user={selectedUser}
              variant="icon"
            />
          </span>
        )}
      </div>
    </div>
  );
};

export default ActionButtons;
